import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import StatusSubtitle from '../../UISystem/StatusSubtitle';
import { IconCircleButton } from '../../UISystem/Icons/IconCircleButton';
import RetryImage from '../RetryImage';
import { GroupChip, GroupOverflowChip } from '../../UISystem/GroupChip';
import { getInitials } from '../../../utils/connectionName';

/**
 * ConnectionCard - A card component for displaying connection information
 *
 * Variants:
 * - 'connection' (default, redesigned to the 2026-07-18 card mock): name +
 *   nickname inline, italic venue + relative day line, group chips,
 *   location/hometown slot. Note and title/company rows retired — that info
 *   lives on the detail page; the nickname carries the descriptor job.
 * - 'chat': Name, last message plain text, green location badge, card click → chat
 */
const ConnectionCard = ({
  name,
  isPlaceholderName = false,
  location,
  photoUrl,
  timestamp,
  recentMessage,
  isMessageFromUser = false,
  hasUnreadMessage = false,
  isManualContact = false,
  isRoyaleUser = false,
  liveNeighborhood,
  onProfileClick,
  onCardClick,
  variant = 'connection',
  // Connection variant props
  nickname = null,
  photoThumbUrl = null,
  // Chat re-entry. RESTORED 2026-08-16 (user ruling) after being dropped from
  // this card at e885230, the Aug-13 People-page pass — the two People pages
  // diverged there, and a Simple profile arriving from the Anon page (which
  // kept it) lost the door back into the conversation one tap later. Callers
  // that have no chat to open, or run a sans-chat variant where chat is
  // dormant, simply pass no handler.
  onChatClick = null,
  groups = [],
  locationDisplay = null,
  hometown = null,
}) => {
  const initials = getInitials(name);

  // Avatar colors: tan/beige for manual contacts, gray for live connections
  const avatarBgColor = isManualContact ? '#D4CAC0' : '#C7C7CC';

  const isChat = variant === 'chat';
  const handleContentClick = isChat ? onCardClick : onProfileClick;

  // ── Chat variant (unchanged) ──
  if (isChat) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          padding: '4px 15px 16px 15px',
          boxSizing: 'border-box',
          borderBottom: '1px solid #EEEEEE',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <tbody>
            <tr>
              {/* Profile image cell */}
              <td style={{ width: '52px', verticalAlign: 'top' }}>
                <div
                  onClick={onProfileClick}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    padding: isRoyaleUser ? '2px' : '0',
                    border: isRoyaleUser ? '2px solid #999' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      backgroundColor: avatarBgColor,
                    }}
                  >
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : isPlaceholderName ? (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <User size={24} color="#fff" weight="regular" />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          fontWeight: '400',
                          color: '#fff',
                        }}
                      >
                        {initials}
                      </div>
                    )}
                  </div>
                </div>
              </td>

              {/* Content cell */}
              <td
                onClick={handleContentClick}
                style={{
                  verticalAlign: 'top',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textAlign: 'left',
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {/* Name */}
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: isPlaceholderName ? '400' : '500',
                    color: isPlaceholderName ? '#8E8E93' : '#000',
                    marginBottom: '4px',
                    lineHeight: '1.2',
                  }}
                >
                  {name || 'Unknown'}
                </div>

                {/* Last message */}
                {recentMessage && (
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: '300',
                      color: '#8E8E93',
                      lineHeight: '1.3',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      paddingRight: '6px',
                    }}
                  >
                    {hasUnreadMessage && !isMessageFromUser && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#48C0FC',
                          marginRight: '6px',
                          verticalAlign: 'middle',
                        }}
                      />
                    )}
                    {recentMessage}
                  </div>
                )}

                {/* Green location badge */}
                {liveNeighborhood && (
                  <div
                    style={{
                      marginTop: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      backgroundColor: '#E8F5E9',
                      borderRadius: '14px',
                      width: 'fit-content',
                    }}
                  >
                    <MapPin size={14} color="#4CAF50" weight="light" style={{ flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        color: '#2E7D32',
                        lineHeight: '1.3',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {liveNeighborhood}
                    </span>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Timestamp */}
        {timestamp && (
          <div
            style={{
              position: 'absolute',
              top: '0px',
              right: '15px',
              fontSize: '13px',
              color: '#8E8E93',
              fontWeight: '400',
              textAlign: 'right',
            }}
          >
            {timestamp}
          </div>
        )}
      </div>
    );
  }

  // ── Connection variant (redesigned to the 2026-07-18 card mock) ──

  // Group chips (cap at 3 + overflow)
  const MAX_CHIPS = 3;
  const visibleGroups = groups.slice(0, MAX_CHIPS);
  const overflowCount = groups.length - MAX_CHIPS;

  // Venue + relative day — one quiet italic line (the shipped PostCard /
  // AnonPreviewCard meta-line pattern)
  const metaLine = [location, timestamp].filter(Boolean).join(', ');

  return (
    <div
      onClick={onProfileClick}
      style={{
        width: '100%',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: '#F9F9FB',
        borderRadius: '16px',
        cursor: 'pointer',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Card content: avatar column + text column */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {/* Avatar */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              padding: isRoyaleUser ? '2px' : '0',
              border: isRoyaleUser ? '2px solid #999' : 'none',
              backgroundColor: isRoyaleUser ? '#FFFFFF' : 'transparent',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: avatarBgColor,
              }}
            >
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : isPlaceholderName ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <User size={24} color="#fff" weight="regular" />
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#fff',
                  }}
                >
                  {initials}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Text column — all rows align naturally */}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          {/* Row 1: Name + nickname inline (gray, after the name; the nickname
              truncates first — the name leads) */}
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: isPlaceholderName ? '400' : '500',
                color: isPlaceholderName ? '#8E8E93' : '#000',
                lineHeight: '1.2',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                maxWidth: nickname ? '65%' : '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name || 'Unknown'}
            </div>
            {nickname && (
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '15px',
                  color: '#8E8E93',
                  fontWeight: '400',
                  lineHeight: '1.2',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                {nickname}
              </span>
            )}
          </div>

          {/* Row 2: Venue + relative day — quiet italic meta line */}
          {metaLine && (
            <div
              style={{
                marginTop: '2px',
                fontSize: '13px',
                color: '#8E8E93',
                fontStyle: 'italic',
                fontWeight: '400',
                lineHeight: '1.3',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {metaLine}
            </div>
          )}

          {/* Row 2.5: Shared-photo thumbnail (sans-chat host flow, mock 7a) —
              the visual receipt that a shared photo actually posted */}
          {photoThumbUrl && (
            <div style={{ marginTop: '8px' }}>
              <RetryImage
                src={photoThumbUrl}
                alt=""
                style={{
                  width: '72px',
                  height: '48px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  display: 'block',
                }}
              />
            </div>
          )}

          {/* Row 3: Group chips */}
          {groups.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                marginTop: '6px',
              }}
            >
              {visibleGroups.map((group) => (
                <GroupChip key={group.id} group={group} />
              ))}
              {overflowCount > 0 && <GroupOverflowChip count={overflowCount} />}
            </div>
          )}

          {/* Row 4: Location slot — live neighborhood > hometown */}
          {(locationDisplay || hometown) && (
            <div style={{ marginTop: '4px' }}>
              <StatusSubtitle
                locationDisplay={locationDisplay}
                hometown={hometown}
                size={14}
                showActivity={false}
              />
            </div>
          )}
        </div>

        {/* Chat re-entry — bare icon, vertically centred, matching
            AnonPreviewCard exactly so the two People pages read as one place. */}
        {onChatClick && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onChatClick();
            }}
            style={{ flexShrink: 0, alignSelf: 'center', display: 'flex' }}
          >
            <IconCircleButton
              icon={PaperPlaneTilt}
              size={20}
              buttonSize={40}
              iconColor="#555555"
              iconWeight="regular"
              aria-label="Open chat"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionCard;
